import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { AlertCircle, BadgeCheck, BadgeInfo, Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { UPDATE_USER_BASE_URL } from "../lib/ApiConfig";
import UserService from "../lib/UserService";
import track from "../lib/analytics/events";
import ContactVerificationModal from "./ContactVerificationModal";
import { OwnliButton } from "./ownli/OwnliScreenWrapper";
const ContactInfoScreen = ({ userData, onComplete, onBack }) => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [serverUser, setServerUser] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    unit: "",
    email: "",
    phone: "",
  });

  // RESTORED: Detailed address components from Old File
  const [addressComponents, setAddressComponents] = useState({
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [placesAutocomplete, setPlacesAutocomplete] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // Auth & Verification State
  const [authMethod, setAuthMethod] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // Verification Modal State
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationType, setVerificationType] = useState(null);
  const [verificationError, setVerificationError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const addressInputRef = useRef(null);

  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [formData, addressComponents, authMethod]);

  const constructFullAddress = (components) => {
    if (!components) return "";
    const parts = [];
    if (components.addressLine1) parts.push(components.addressLine1);
    if (components.addressLine2) parts.push(components.addressLine2);
    if (components.city) parts.push(components.city);
    if (components.state) parts.push(components.state);
    if (components.zip) parts.push(components.zip);
    return parts.join(", ");
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setUserLoading(true);
        const token = await getIdToken();
        if (!token) throw new Error("Authentication required");

        // Fetch User Profile from Old API
        const response = await fetch(`${UPDATE_USER_BASE_URL}/api/users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        });

        if (!response.ok) {
          // If API fails, we might fall back, but logging error first
          console.error(`Failed to fetch user data: ${response.status}`);
          handleFallbackData(); // Attempt to load from Firebase/Session if API fails
          return;
        }

        const apiData = await response.json();
        setServerUser(apiData);
        console.log("API userData", apiData);

        // Hydrate Form with API Data
        setFormData({
          firstName: apiData.firstName || "",
          lastName: apiData.lastName || "",
          address:
            constructFullAddress(apiData.address) ||
            apiData.address?.fullAddress ||
            "",
          unit: apiData.address?.addressLine2 || "",
          email: apiData.email || "",
          phone: apiData.phone?.startsWith("+1")
            ? apiData.phone.substring(2)
            : apiData.phone || "",
        });

        // Hydrate Address Components
        if (apiData.address) {
          setAddressComponents({
            addressLine1: apiData.address.addressLine1 || "",
            city: apiData.address.city || "",
            state: apiData.address.state || "",
            zip: apiData.address.zip || "",
            country: apiData.address.country || "",
          });
        }

        // Set Verification Flags
        setEmailVerified(apiData.emailVerified || false);
        setPhoneVerified(apiData.phoneVerified || false);

        // Determine Auth Method
        if (apiData.authMethod) {
          setAuthMethod(apiData.authMethod);
        } else if (user) {
          setAuthMethod(user.phoneNumber ? "phone" : "email");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        handleFallbackData();
      } finally {
        setUserLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    } else {
      handleFallbackData();
      setUserLoading(false);
    }
  }, [user, getIdToken]);

  // Fallback Logic (Kept mostly as helper)
  const handleFallbackData = () => {
    const savedData = sessionStorage.getItem("contactInfoData");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData((prev) => ({ ...prev, ...parsed }));
      } catch (e) {}
    } else if (user) {
      setAuthMethod(user.phoneNumber ? "phone" : "email");
      if (user.email) setFormData((p) => ({ ...p, email: user.email }));
      // Add other firebase fallbacks if needed
    }
  };

  // Google Maps Logic
  useEffect(() => {
    if (window.google && window.google.maps) {
      setGoogleMapsLoaded(true);
      return;
    }
    window.initGoogleMapsAutocomplete = () => {
      setGoogleMapsLoaded(true);
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDG7_6lxN0J7dEDP1maUwBzoOlWd0wqpFQ&libraries=places&callback=initGoogleMapsAutocomplete`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      delete window.initGoogleMapsAutocomplete;
    };
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (googleMapsLoaded && addressInputRef.current) {
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ["address"] }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.formatted_address) {
            setFormData((prev) => ({
              ...prev,
              address: place.formatted_address,
            }));

            // Extract address components
            const addressData = extractAddressComponents(place);
            setAddressComponents(addressData);

            // Clear any address errors when valid address is selected
            setErrors((prev) => ({ ...prev, address: null }));
          }
        });

        setPlacesAutocomplete(autocomplete);
      } catch (error) {
        console.error("Error initializing Google Maps Autocomplete:", error);
      }
    }
  }, [googleMapsLoaded, addressInputRef.current]);

  // RESTORED: Detailed Extraction Logic from Old File
  const extractAddressComponents = (place) => {
    const components = {
      streetNumber: "",
      route: "",
      addressLine1: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    };

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes("street_number")) {
          components.streetNumber = component.long_name;
        } else if (types.includes("route")) {
          components.route = component.long_name;
        } else if (types.includes("locality")) {
          components.city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          // Use short_name for state to get the 2-letter code
          components.state = component.short_name;
        } else if (types.includes("postal_code")) {
          components.zip = component.long_name;
        } else if (types.includes("country")) {
          components.country = component.long_name;
        }
      }
    }

    // Combine street number and route for addressLine1
    if (components.streetNumber && components.route) {
      components.addressLine1 = `${components.streetNumber} ${components.route}`;
    } else if (components.route) {
      components.addressLine1 = components.route;
    } else {
      components.addressLine1 = place.formatted_address;
    }

    return components;
  };

  // Verification Logic (Kept from Current File)
  const openVerificationModal = async (type) => {
    // Normalize phone number comparison
    const normalizePhone = (phone) => {
      if (!phone) return "";

      // Remove all non-digit characters
      const digitsOnly = phone.replace(/\D/g, "");

      // Handle different formats
      // Remove leading +1 or 1 if present
      const normalized = digitsOnly.replace(/^(\+?1?)/, "");

      return normalized;
    };

    const normalizedServerPhone = normalizePhone(serverUser.phone || "");
    const normalizedFormPhone = normalizePhone(formData.phone || "");

    console.log(normalizedServerPhone);
    console.log(normalizedFormPhone);

    // Check conditions based on verification type
    const isValidVerification =
      type === "email"
        ? serverUser.email === formData.email
        : normalizedServerPhone === normalizedFormPhone;

    if (!isValidVerification) {
      // Show error that contact info doesn't match server record
      setErrors((prev) => ({
        ...prev,
        [type]: `Please save the ${type} before verifying`,
      }));
      return;
    }

    if (
      (type === "email" && !formData.email) ||
      (type === "phone" && !formData.phone)
    )
      return;

    // First, validate the specific field
    const validateField =
      type === "email"
        ? () => isValidEmail(formData.email)
        : () => isValidPhone(formData.phone);
    if (!validateField()) {
      // Set an error for the specific field
      setErrors((prev) => ({
        ...prev,
        [type]:
          type === "email"
            ? "Please enter a valid email before verification"
            : "Please enter a valid phone number before verification",
      }));
      return;
    }

    try {
      const token = await getIdToken();
      setVerificationType(type);
      setVerificationError("");
      setVerificationModalOpen(true);
      setIsSendingCode(true);

      const method = type === "email" ? "Email" : "Phone";

      await UserService.sendCustomerVerificationCode({
        method,
        firebaseToken: token,
      });
    } catch (error) {
      console.error("Error sending verification code:", error);
      setVerificationError(
        error.message || "Failed to send verification code."
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerificationSubmit = async (code) => {
    if (!code || code.length !== 5 || !verificationType) return;
    track.contactVerifyClicked({ type: verificationType });
    try {
      setIsVerifying(true);

      const token = await getIdToken();
      const method = verificationType === "email" ? "Email" : "Phone";

      const result = await UserService.verifyCustomerVerificationCode({
        method,
        code,
        firebaseToken: token,
      });

      const status = result?.status || result?.data?.status;

      if (status === "CodeVerified") {
        if (verificationType === "email") setEmailVerified(true);
        if (verificationType === "phone") setPhoneVerified(true);
        setVerificationModalOpen(false);
      } else {
        setVerificationError("Invalid code or expired.");
      }
    } catch (error) {
      setVerificationError(error.message || "Verification failed.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Reset address components if user manually types in address field
    if (name === "address") {
      setAddressComponents({
        addressLine1: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      });
    }

    // Reset verification status if they change the verified field
    if (name === "email" && emailVerified) setEmailVerified(false);
    if (name === "phone" && phoneVerified) setPhoneVerified(false);

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim())
      newErrors.lastName = "Last name is required";

    // Strict Address Validation based on components
    // Remove the address validation requirements
    // if (!formData.address?.trim()) {
    //   newErrors.address = "Address is required";
    // } else if (
    //   !addressComponents.addressLine1 ||
    //   !addressComponents.city ||
    //   !addressComponents.state ||
    //   !addressComponents.zip
    // ) {
    //   // Optional: you can relax this if you want to allow manual entry without Google Maps
    //   // newErrors.address = "Please select a valid address with street, city, state, and ZIP";
    // }

    // If address is provided, still recommend using Google Maps autocomplete
    // If address is entered, it MUST be validated by Google Maps
    if (formData.address?.trim()) {
      if (
        !addressComponents.addressLine1?.trim() ||
        !addressComponents.city?.trim() ||
        !addressComponents.state?.trim() ||
        !addressComponents.zip?.trim()
      ) {
        newErrors.address =
          "Please select a valid address using Google Maps autocomplete";
      }
    }
    if (authMethod === "phone") {
      if (!formData.email?.trim()) newErrors.email = "Email is required";
      else if (formData.email && !isValidEmail(formData.email))
        newErrors.email = "Invalid email";
    }

    if (authMethod === "email") {
      if (!formData.phone?.trim()) newErrors.phone = "Phone is required";
      else if (formData.phone && !isValidPhone(formData.phone))
        newErrors.phone = "Invalid phone";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^\d{10}$/.test(phone);

  const handleRewards = () => {
    navigate("/rewards", { replace: true });
  };
  // RESTORED: Submit Logic from Old File (using update-me endpoint and object structure)
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    sessionStorage.setItem("contactInfoData", JSON.stringify(formData));
    sessionStorage.setItem(
      "contactInfoAddressComponents",
      JSON.stringify(addressComponents)
    );

    try {
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      track.identify(user.id || user.uid, {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
      });

      let safeState = addressComponents.state || "";
      if (safeState.length > 2) {
        safeState = safeState.substring(0, 2).toUpperCase();
      }

      let apiPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        isEmailVerified: emailVerified,
        isPhoneVerified: phoneVerified,
        address: {
          addressLine1: addressComponents.addressLine1 || formData.address,
          addressLine2: formData.unit,
          city: addressComponents.city,
          state: safeState,
          zip: addressComponents.zip,
          country: addressComponents.country,
          fullAddress: formData.address,
        },
        email: formData.email,
        phone: formData.phone,
      };
      if (!formData.address?.trim()) {
        apiPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          isEmailVerified: emailVerified,
          isPhoneVerified: phoneVerified,
          email: formData.email,
          phone: formData.phone,
        };
      }
      console.log("Submitting to Old API:", apiPayload);

      // Call Update-Me Endpoint
      const response = await fetch(
        `${UPDATE_USER_BASE_URL}/api/users/update-me`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(apiPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        let errorMessage =
          errorData.error_message || "Failed to update profile";
        if (errorData.error_code === "ERROR_USER_EXISTS") {
          errorMessage = "A user already exists with this phone or email";
        }
        throw new Error(errorMessage);
      }

      const updatedUser = await response.json();

      // Complete
      if (onComplete) {
        onComplete(updatedUser);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrors({ general: error.message || "Failed to save information." });
    } finally {
      setIsLoading(false);
    }
  };

  if (userLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-crimsonGlory border-t-transparent rounded-full"></div>
          <span className="ml-3">Loading your information...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 stagger-children">
      <Card className="border-none">
        <div className="p-4 pb-6 text-center">
          <h1 className="text-xl uppercase font-bold mb-5">
            Let’s Get to know You
          </h1>
        </div>
        <CardContent className="space-y-4 px-4">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label
                htmlFor="firstName"
                className="text-sm font-medium text-darkGrey"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <div className="relative !mt-1">
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full font-medium ${
                    errors.firstName ? "border-red-500" : ""
                  }`}
                  placeholder="First Name"
                />
              </div>
              {errors.firstName && (
                <p className="text-red-500 text-sm">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="lastName"
                className="text-sm font-medium text-darkGrey"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <div className="relative !mt-1">
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full font-medium ${
                    errors.lastName ? "border-red-500" : ""
                  }`}
                  placeholder="Last Name"
                />
              </div>
              {errors.lastName && (
                <p className="text-red-500 text-sm">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Address Field */}
          <div className="space-y-2">
            <label
              htmlFor="address"
              className="text-sm font-medium text-darkGrey"
            >
              Address (Optional)
            </label>
            <div className="relative !mt-1">
              {/* <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" /> */}
              <input
                id="address"
                name="address"
                type="text"
                ref={addressInputRef}
                value={formData.address}
                onChange={handleInputChange}
                className={`w-full font-medium ${
                  errors.address ? "border-red-500" : ""
                }`}
                placeholder="Current address"
              />
            </div>
            {!googleMapsLoaded && (
              <div className="text-xs text-gray-500">
                Loading Google Maps...
              </div>
            )}
            {errors.address && (
              <p className="text-red-500 text-sm">{errors.address}</p>
            )}
          </div>

          {/* Unit Field (Optional) - Kept from Old Logic structure */}
          <div className="space-y-2">
            <label htmlFor="unit" className="text-sm font-medium text-darkGrey">
              Unit / Apt (Optional)
            </label>
            <input
              id="unit"
              name="unit"
              value={formData.unit}
              onChange={handleInputChange}
              className="w-full font-medium border rounded p-2"
              placeholder="Apt #"
            />
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-1 gap-5">
            {/* Email */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-darkGrey flex items-center gap-2"
              >
                Email
                {authMethod === "phone" && (
                  <span className="text-red-500">*</span>
                )}
                {formData.email &&
                  (emailVerified ? (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <BadgeCheck className="w-4 h-4 text-green-600" />
                      <span className="mt-0.5">Verified</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openVerificationModal("email")}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 underline-offset-2 hover:underline underline"
                    >
                      <BadgeInfo className="w-4 h-4 text-red-600" />
                      <span className="mt-0.5">Verify now</span>
                    </button>
                  ))}
              </label>
              <div className="relative !mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full font-medium ${
                    errors.email ? "border-red-500" : ""
                  }`}
                  placeholder="Email"
                  readOnly={authMethod === "email"}
                  disabled={authMethod === "email"}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="text-sm font-medium text-darkGrey flex items-center gap-2"
              >
                Phone Number
                {authMethod === "email" && (
                  <span className="text-red-500">*</span>
                )}
                {formData.phone &&
                  (phoneVerified ? (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <BadgeCheck className="w-4 h-4 text-green-600" />
                      <span className="mt-0.5">Verified</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openVerificationModal("phone")}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 underline-offset-2 hover:underline  underline"
                    >
                      <BadgeInfo className="w-4 h-4 text-red-600" />
                      <span className="mt-0.5">Verify now</span>
                    </button>
                  ))}
              </label>
              <div className="relative !mt-1">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, phone: value }));
                    if (errors.phone)
                      setErrors((prev) => ({ ...prev, phone: null }));
                  }}
                  className={`w-full font-medium ${
                    errors.phone ? "border-red-500" : ""
                  }`}
                  placeholder="Phone number"
                  maxLength={10}
                  readOnly={authMethod === "phone"}
                  disabled={authMethod === "phone"}
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-sm">{errors.phone}</p>
              )}
            </div>
          </div>

          {errors.general && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p>{errors.general}</p>
            </div>
          )}

          <OwnliButton
            onClick={() => {
              track.contactSaveClicked({ step_name: "contact-info" });
              handleSubmit();
            }}
            disabled={isLoading || !isFormValid}
            className={`w-full flex items-center justify-center gap-2 py-3 px-4 transition-colors`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span className="font-bold">Save</span>
            )}
          </OwnliButton>
          <button
            onClick={() => {
              handleRewards();
            }}
            className={`underline w-full flex items-center justify-center gap-2 py-3 px-4 transition-colors`}
          >
            <span className="font-bold">Go to Rewards Center</span>
          </button>
        </CardContent>
      </Card>

      {/* Verification Modal (Maintained from Current File) */}
      {verificationModalOpen && (
        <ContactVerificationModal
          loading={isVerifying}
          email={formData.email}
          phone={formData.phone}
          sending={isSendingCode}
          error={verificationError}
          type={verificationType}
          open={verificationModalOpen}
          onVerify={handleVerificationSubmit}
          onOpenChange={(open) => {
            if (!open) setVerificationModalOpen(false);
          }}
          sendingIcon={
            isSendingCode ? (
              <Loader2 className="animate-spin w-4 h-4 mr-2" />
            ) : null
          }
          verifyingIcon={
            isVerifying ? (
              <Loader2 className="animate-spin w-4 h-4 mr-2" />
            ) : null
          }
        />
      )}
    </div>
  );
};

export default ContactInfoScreen;
